import { Link } from '@tanstack/react-router';
import { cmsPageImageUrl, cmsPlainTextExcerpt, unescapeCmsText, type CmsPage } from '@/domain';
import { cn } from '@/lib/cn';
import { buttonVariants } from '@/ui/common';

export interface PromoDetailPanelProps {
  page: CmsPage;
  usersBaseUrl: string;
  accountGuid: string;
  label: (key: string, fallback: string) => string;
  className?: string;
}

export function PromoDetailPanel({
  page,
  usersBaseUrl,
  accountGuid,
  label,
  className,
}: PromoDetailPanelProps) {
  const title = page.Title?.trim() || page.Url?.trim() || label('Promotions', 'Promotions');
  const htmlBody = unescapeCmsText(page.Text);
  const subtitle = cmsPlainTextExcerpt(page.Text, 120);
  const imageUrl =
    page.Image?.trim() && accountGuid
      ? cmsPageImageUrl(usersBaseUrl, accountGuid, page.Id, page.Image)
      : null;

  return (
    <div className={cn('mx-auto flex w-full max-w-[1040px] flex-col gap-6', className)}>
      <Link
        to="/promotions"
        className="w-fit text-body-m text-text-primary hover:text-text-secondary"
      >
        ← {label('AllPromotions', 'All promotions')}
      </Link>

      <div className="relative flex min-h-[180px] flex-col items-start justify-end gap-3 overflow-hidden rounded-md p-5 lg:min-h-[240px] lg:p-7">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="absolute inset-0 size-full rounded-md object-cover"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 rounded-md bg-bg-surface" />
        )}
        <div
          className="absolute inset-0 rounded-md bg-gradient-to-b from-[rgba(10,18,31,0.15)] via-[rgba(10,18,31,0.55)] to-[rgba(10,18,31,0.82)]"
          aria-hidden
        />
        <h1 className="relative z-10 text-[24px] leading-8 font-bold tracking-[-0.24px] text-text-primary">
          {title}
        </h1>
        {subtitle ? (
          <p className="relative z-10 text-body-l text-text-secondary">{subtitle}</p>
        ) : null}
      </div>

      {htmlBody ? (
        <div
          className="prose-promo text-body-m text-text-secondary [&_a]:text-text-primary [&_a]:underline [&_h1]:mb-2 [&_h1]:text-heading-s [&_h1]:text-text-primary [&_h2]:mb-2 [&_h2]:text-heading-s [&_h2]:text-text-primary [&_h3]:mb-2 [&_h3]:text-heading-s [&_h3]:text-text-primary [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: htmlBody }}
        />
      ) : null}

      <Link
        to="/profile/deposits"
        className={cn(buttonVariants({ variant: 'primary', size: 'lg' }), 'w-fit')}
      >
        {label('ClaimDeposit', 'Claim / Deposit')}
      </Link>

      <p className="text-body-s text-text-secondary">
        {label(
          'PromoTermsDisclaimer',
          'Terms & conditions apply. 18+. Please gamble responsibly.',
        )}
      </p>
    </div>
  );
}
